import type { SceneMeta } from '../core/types';
import { BRANDS } from '../brand/brands';
import { GROUP_ORDER, PRODUCT_ORDER, groups, products } from './catalogue';
import { presentationConfig, type PresentationConfig } from './config';
import { RUNNING_ORDER } from './order';
import type { ModuleId, PresentationContext, PresentationModule, ResolvedScene, SceneDefinition } from './types';
import { MODULES } from '../modules';

/** Turns the manifest into the set of enabled module ids. Specialisations require their parent. */
export function enabledModules(config: PresentationConfig): Set<ModuleId> {
  const m = config.modules;
  const on = new Set<ModuleId>();
  const add = (id: ModuleId, flag: boolean): void => { if (flag) on.add(id); };
  add('intro', m.intro);
  add('mint', m.mint.enabled);
  add('mint-it', m.mint.enabled && m.mint.specialisations.it);
  add('mint-travel', m.mint.enabled && m.mint.specialisations.travel);
  add('mint-business', m.mint.enabled && m.mint.specialisations.business);
  add('ecosystem', m.ecosystem);
  add('research-space', m.researchSpace.enabled);
  add('native', m.native.enabled);
  add('carbonmove', m.carbonMove.enabled);
  add('climateviewpoint', m.climateViewPoint.enabled);
  add('etrust', m.eTrust.enabled);
  add('shared-capabilities', m.sharedCapabilities);
  add('technology', m.technology);
  add('process', m.process);
  add('future', m.future);
  add('closing', m.closing);
  return on;
}

/** Product modules that can be listed in a `?modules=` rehearsal override. */
const PRODUCT_MODULES: readonly ModuleId[] = ['mint', 'mint-it', 'mint-travel', 'mint-business', 'research-space', 'native', 'carbonmove', 'climateviewpoint', 'etrust'];

/**
 * Applies a product filter to a config: only the listed product modules stay on (if they are on
 * in the base config); structural modules are unchanged. Used by the dev URL override and tests.
 */
export function withProducts(base: PresentationConfig, keep: readonly ModuleId[]): PresentationConfig {
  const has = (id: ModuleId): boolean => keep.includes(id);
  const m = base.modules;
  return {
    modules: {
      ...m,
      mint: {
        enabled: m.mint.enabled && has('mint'),
        specialisations: {
          it: m.mint.specialisations.it && has('mint-it'),
          travel: m.mint.specialisations.travel && has('mint-travel'),
          business: m.mint.specialisations.business && has('mint-business'),
        },
      },
      researchSpace: { enabled: m.researchSpace.enabled && has('research-space') },
      native: { enabled: m.native.enabled && has('native') },
      carbonMove: { enabled: m.carbonMove.enabled && has('carbonmove') },
      climateViewPoint: { enabled: m.climateViewPoint.enabled && has('climateviewpoint') },
      eTrust: { ...m.eTrust, enabled: m.eTrust.enabled && has('etrust') },
    },
  };
}

/** The config for this run: the manifest, optionally narrowed by `?modules=` during development. */
export function resolveConfig(search = ''): PresentationConfig {
  const requested = new URLSearchParams(search).get('modules');
  if (!requested) return presentationConfig;
  const keep = requested.split(',').map((s) => s.trim()).filter((s): s is ModuleId => (PRODUCT_MODULES as readonly string[]).includes(s));
  return withProducts(presentationConfig, keep);
}

export function createContext(config: PresentationConfig): PresentationContext {
  const modules = enabledModules(config);
  const isEnabled = (id: ModuleId): boolean => modules.has(id);
  const activeProducts = PRODUCT_ORDER.map((id) => products[id]).filter((p) => isEnabled(p.module));
  const activeGroups = GROUP_ORDER.map((id) => groups[id]).filter((g) => activeProducts.some((p) => p.group === g.id));
  return { modules, isEnabled, products: activeProducts, groups: activeGroups };
}

function allDefinitions(): Map<string, SceneDefinition> {
  const map = new Map<string, SceneDefinition>();
  MODULES.forEach((module: PresentationModule) => module.scenes.forEach((scene) => {
    if (map.has(scene.id)) throw new Error(`Duplicate scene id: ${scene.id}`);
    map.set(scene.id, scene);
  }));
  const unordered = [...map.keys()].filter((id) => !RUNNING_ORDER.includes(id));
  if (unordered.length) throw new Error(`Scenes missing from RUNNING_ORDER: ${unordered.join(', ')}`);
  return map;
}

const bridgeText = (scene: SceneDefinition, context: PresentationContext): string =>
  typeof scene.bridgeIn === 'function' ? scene.bridgeIn(context) : scene.bridgeIn;

const clock = (seconds: number): string => `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;

/**
 * Builds the final scene sequence from enabled modules: resolves scripts, derives transitions
 * from whatever comes next, and computes running times. Pure data; nothing is instantiated.
 */
export function buildPresentation(config: PresentationConfig = presentationConfig): ResolvedScene[] {
  const context = createContext(config);
  const definitions = allDefinitions();
  const active = RUNNING_ORDER
    .map((id) => definitions.get(id))
    .filter((d): d is SceneDefinition => !!d && context.isEnabled(d.module) && (d.requires ?? []).every((r) => context.isEnabled(r))
      && (!d.requiresAny || d.requiresAny.some((r) => context.isEnabled(r))));

  let elapsed = 0;
  return active.map((definition, index) => {
    const content = typeof definition.script === 'function' ? definition.script(context) : definition.script;
    const next = active[index + 1];
    const start = elapsed;
    elapsed += definition.seconds;
    const identity = BRANDS[definition.brand];
    const meta: SceneMeta = {
      id: definition.id,
      shortLabel: definition.shortLabel,
      title: definition.title,
      duration: `${clock(start)}–${clock(elapsed)}`,
      accent: definition.accent ?? identity.accent,
      brand: definition.brand,
      tone: identity.tone,
      // Branches belong to the MINT chapter; every other scene is grouped under its brand.
      chapter: definition.brand.startsWith('mint') ? 'MINT' : identity.name.replace('Moderta AI — ', ''),
      cinematic: definition.cinematic,
    };
    return {
      definition,
      meta,
      script: { ...content, transition: next ? bridgeText(next, context) : 'End of presentation.' },
      context,
      index,
    };
  });
}
